import { useAppStore } from "../store";
import type { Space } from "../types";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";
const BASE = `${API_BASE}/api`;

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const token = useAppStore.getState().authToken;
  const authHeaders: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    ...opts,
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  spaces: {
    list: () => request<Space[]>('/spaces'),
    create: (data: { name: string; color: string; private?: boolean }) =>
      request<Space>('/spaces', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, changes: Partial<{ name: string; color: string; private: boolean }>) =>
      request<Space>(`/spaces/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    delete: (id: string, targetSpaceId: string) =>
      request<void>(`/spaces/${id}`, { method: 'DELETE', body: JSON.stringify({ targetSpaceId }) }),
  },
  tasks: {
    list: (params: Record<string, string>) => {
      const qs = new URLSearchParams(params).toString();
      return request<any[]>(`/tasks?${qs}`);
    },
    getById: (id: string) => request<any>(`/tasks/${id}`),
    getSubtasks: (parentId: string) => request<any[]>(`/tasks/${parentId}/subtasks`),
    getDoneSubtaskCount: (parentId: string) => request<{ count: number }>(`/tasks/${parentId}/subtasks/done/count`).then(r => r.count),
    create: (data: any) => request<any>('/tasks', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, changes: any) => request<any>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    delete: (id: string) => request<any>(`/tasks/${id}`, { method: 'DELETE' }),
    moveToTrack: (id: string, trackDate: string) => request<any>(`/tasks/${id}/move-to-track`, { method: 'POST', body: JSON.stringify({ trackDate }) }),
    rollover: (spaceId: string) => request<any>('/tasks/rollover', { method: 'POST', body: JSON.stringify({ spaceId }) }),
  },
  progressUpdates: {
    getByTaskId: (taskId: string) => request<any[]>(`/progress-updates?taskId=${taskId}`),
    getAll: () => request<any[]>('/progress-updates/all'),
    create: (data: any) => request<any>('/progress-updates', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, changes: any) => request<any>(`/progress-updates/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    delete: (id: string) => request<any>(`/progress-updates/${id}`, { method: 'DELETE' }),
  },
  projects: {
    getBySpace: (spaceId: string) => request<any[]>(`/projects?spaceId=${spaceId}`),
    create: (data: any) => request<any>('/projects', { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: string) => request<any>(`/projects/${id}`, { method: 'DELETE' }),
  },
  data: {
    export: () => request<any>('/export'),
    import: (data: any) => request<any>('/import', { method: 'POST', body: JSON.stringify(data) }),
    clear: () => request<any>('/clear', { method: 'POST' }),
  },
  calendarEvents: {
    list: (date: string) => request<any[]>(`/calendar-events?date=${date}`),
  },
  calendar: {
    getConnections: () => request<Array<{ provider: string; email: string; updatedAt: string }>>("/calendar/connections"),
    disconnect: (provider: string) => request<void>(`/calendar/connections/${provider}`, { method: "DELETE" }),
    getEvents: (date: string) => request<any[]>(`/calendar/events?date=${date}`),
  },
  calendarNotes: {
    list: (date: string) => request<any[]>(`/calendar-notes?date=${date}`),
    search: (q: string) => request<any[]>(`/calendar-notes/search?q=${encodeURIComponent(q)}`),
    getByKey: (meetingKey: string) => request<any>(`/calendar-notes/by-key/${meetingKey}`),
    create: (data: { meetingKey: string; title: string; content: string; date: string }) =>
      request<any>('/calendar-notes', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, changes: { content: string }) =>
      request<any>(`/calendar-notes/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    delete: (id: string) => request<any>(`/calendar-notes/${id}`, { method: 'DELETE' }),
  },
  timelineBlocks: {
    list: (date: string) => request<any[]>(`/timeline-blocks?date=${date}`),
    listRange: (startDate: string, endDate: string) =>
      request<any[]>(`/timeline-blocks/range?startDate=${startDate}&endDate=${endDate}`),
    upcoming: () => request<any[]>(`/timeline-blocks/upcoming`),
    starting: (date: string, sinceTime: string) => request<any[]>(`/timeline-blocks/starting?date=${date}&sinceTime=${sinceTime}`),
    create: (data: { taskId: string; date: string; scheduledStartTime: string; scheduledDuration?: number }) =>
      request<any>('/timeline-blocks', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, changes: { scheduledStartTime?: string; scheduledDuration?: number; date?: string }) =>
      request<any>(`/timeline-blocks/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    delete: (id: string) => request<void>(`/timeline-blocks/${id}`, { method: 'DELETE' }),
    startingSoon: (date: string) => request<any[]>(`/timeline-blocks/starting-soon?date=${date}`),
    markNotified: (id: string) => request<any>(`/timeline-blocks/${id}/notified`, { method: 'POST' }),
  },
  recurring: {
    generate: (spaceId?: string) => request<any>(`/recurring/generate${spaceId ? `?spaceId=${spaceId}` : ''}`, { method: 'POST' }),
    templates: (spaceId?: string) => request<any[]>(`/recurring/templates${spaceId ? `?spaceId=${spaceId}` : ''}`),
    pause: (id: string) => request<any>(`/recurring/${id}/pause`, { method: 'POST' }),
    resume: (id: string) => request<any>(`/recurring/${id}/resume`, { method: 'POST' }),
    delete: (id: string) => request<any>(`/recurring/${id}`, { method: 'DELETE' }),
    update: (id: string, changes: Record<string, unknown>) => request<any>(`/recurring/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes) }),
  },
  preferences: {
    get: () => request<{ timezone: string }>('/preferences'),
    update: (data: { timezone: string }) =>
      request<{ timezone: string }>('/preferences', { method: 'PUT', body: JSON.stringify(data) }),
  },
};
